import {keepPreviousData, useQuery, useQueryClient} from '@tanstack/react-query';
import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Breadcrumbs} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {Input} from '~/components/Controls/Input';
import {SelectInput} from '~/components/Controls/Select';
import {ToggleGroup} from '~/components/Controls/ToggleGroup';
import {SelectTags} from '~/components/Controls/Tags/api';
import {useModals} from '~/components/Modal';
import {Pagination} from '~/components/Pagination';
import {Progress} from '~/components/Progress';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {CSV} from '~/lib/csv';
import {useDataSource} from '~/lib/dataSource';
import {getApiErrorMessage} from '~/lib/errors/apiError';
import {Intent} from '~/lib/intent';
import {notify} from '~/lib/notify';
import {BiddersAbility} from '~/modules/Bidders/ability';
import {biddersAdapter} from '~/modules/Bidders/adapters';
import {fetchBidder, fetchBiddersPage, IBiddersFilter, prepareBidderSubmitData, replanSpendLimits} from '~/modules/Bidders/api';
import {BiddersTypeNavigation} from '~/modules/Bidders/components/BiddersTypeNavigation';
import {IReplanSpendLimitsModalProps, ReplanSpendLimitsModal} from '~/modules/Bidders/components/ReplanSpendLimitsModal';
import {usePersistedBiddersFilter} from '~/modules/Bidders/hooks/usePersistedBiddersFilter';
import {biddersLinks} from '~/modules/Bidders/links';
import {IBidderForm} from '~/modules/Bidders/forms/BidderForm';
import {IBiddersForm, IBiddersFormActions} from '~/modules/Bidders/forms/BiddersForm';
import {IBidder} from '~/modules/Bidders/models/Bidder';
import {IBidderSummary} from '~/modules/Bidders/models/BidderSummary';
import {BiddersTable} from '~/modules/Bidders/table/bidders';
import style from './index.module.scss';

const LazyBidderForm = React.lazy(() =>
    import('~/modules/Bidders/forms/BidderForm').then(module => ({default: module.BidderForm}))
);

const LazyBiddersForm = React.lazy(() =>
    import('~/modules/Bidders/forms/BiddersForm').then(module => ({default: module.BiddersForm}))
);

const BidderFormModal = (props: any) => (
    <React.Suspense fallback={<Progress/>}>
        <LazyBidderForm {...props}/>
    </React.Suspense>
);

const BiddersFormModal = (props: any) => (
    <React.Suspense fallback={<Progress/>}>
        <LazyBiddersForm {...props}/>
    </React.Suspense>
);

const normalizeSearch = (value?: string) => (value ?? '').trim();
const DEFAULT_PAGE_SIZE = 25;
const BIDDERS_PAGE_QUERY_KEY = 'bidders-page';

type IBiddersPageQueryFilter = Pick<IBiddersFilter, 'active' | 'bidderSearch' | 'agencySearch' | 'auctionCategory' | 'tag'> & {
    pageNo: number;
    pageSize: number;
};

const getBiddersPageQueryKey = (filter: IBiddersPageQueryFilter) => [BIDDERS_PAGE_QUERY_KEY, filter] as const;

export const BiddersView = () => {
    const ability = useAbility(BiddersAbility);
    const navigate = useNavigate();
    const dataSource = useDataSource(biddersAdapter);
    const modals = useModals();
    const queryClient = useQueryClient();
    const [filter, setFilter] = usePersistedBiddersFilter();
    const [paginator, setPaginator] = useState<{ page: number; pageSize: number }>({
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE
    });

    const normalizedBidderSearch = useMemo(() => normalizeSearch(filter.bidderSearch), [filter.bidderSearch]);
    const normalizedAgencySearch = useMemo(() => normalizeSearch(filter.agencySearch), [filter.agencySearch]);
    const [debouncedSearch, setDebouncedSearch] = useState({
        bidder: normalizedBidderSearch,
        agency: normalizedAgencySearch
    });

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setDebouncedSearch({
                bidder: normalizedBidderSearch,
                agency: normalizedAgencySearch
            });
        }, 300);
        return () => window.clearTimeout(timeout);
    }, [normalizedBidderSearch, normalizedAgencySearch]);

    const pageFilter = useMemo<IBiddersPageQueryFilter>(() => ({
        active: filter.active,
        bidderSearch: debouncedSearch.bidder,
        agencySearch: debouncedSearch.agency,
        auctionCategory: filter.auctionCategory ?? null,
        tag: (filter.tag ?? '').trim(),
        pageNo: paginator.page,
        pageSize: paginator.pageSize
    }), [
        filter.active,
        debouncedSearch.bidder,
        debouncedSearch.agency,
        filter.auctionCategory,
        filter.tag,
        paginator.page,
        paginator.pageSize
    ]);

    const biddersQuery = useQuery({
        queryKey: getBiddersPageQueryKey(pageFilter),
        queryFn: ({signal}) => fetchBiddersPage(pageFilter, undefined, {signal}),
        placeholderData: keepPreviousData,
        staleTime: 30 * 1000
    });

    const pageResource = biddersQuery.data ?? {
        items: [],
        count: 0,
        page: paginator.page,
        pageSize: paginator.pageSize
    };

    useEffect(() => {
        dataSource.setRecords(pageResource.items || []);
    }, [pageResource.items]);

    useEffect(() => {
        if (!biddersQuery.data) {
            return;
        }
        const nextPageNo = pageResource.page + 1;
        if (nextPageNo * pageResource.pageSize >= pageResource.count) {
            return;
        }
        const nextPageFilter: IBiddersPageQueryFilter = {
            ...pageFilter,
            pageNo: nextPageNo,
            pageSize: pageResource.pageSize
        };
        queryClient.prefetchQuery({
            queryKey: getBiddersPageQueryKey(nextPageFilter),
            queryFn: ({signal}) => fetchBiddersPage(nextPageFilter, undefined, {signal}),
            staleTime: 30 * 1000
        });
    }, [queryClient, biddersQuery.data, pageFilter, pageResource.page, pageResource.pageSize, pageResource.count]);

    const refreshPagedBidders = () =>
        queryClient.invalidateQueries({queryKey: [BIDDERS_PAGE_QUERY_KEY]});

    const editBidder = modals.create<IBidderForm>(BidderFormModal, {
        onSubmit: (bidder: IBidder, formActions, close) => {
            dataSource.updateRecord(prepareBidderSubmitData(bidder)).then(() => {
                close();
                refreshPagedBidders();
            }).catch(error => {
                formActions.setSubmitting(false);
                formActions.setStatus(getApiErrorMessage(error));
            });
        }
    });

    const bulkItemsText = () => {
        let bulkItems: string[] = [];
        dataSource.state.records.map(record => {
            if (record.check) {
                bulkItems.push(record.title);
            }
        });
        return 'Edit records (' + bulkItems.length + '): ' + bulkItems.join(', ');
    };

    const biddersEdit = modals.create<IBiddersForm>(BiddersFormModal, {
        records: bulkItemsText(),
        onSubmit: (tags: {addTags: string[]; removeTags: string[]}, formActions: IBiddersFormActions, close) => {
            let bulkEdit: any[] = [];
            dataSource.state.records.map(record => {
                if (record.check) {
                    bulkEdit.push({...record});
                }
            });
            bulkEdit.map(record => {
                if (!Array.isArray(record.tags)) {
                    record.tags = [];
                }
                tags.addTags.map((tag: string) => {
                    const tagIndex = record.tags.indexOf(tag);
                    if (tagIndex < 0) {
                        record.tags.push(tag);
                    }
                });
                tags.removeTags.map((tag: string) => {
                    const tagIndex = record.tags.indexOf(tag);
                    if (tagIndex >= 0) {
                        record.tags.splice(tagIndex, 1);
                    }
                });
            });
            dataSource.updateRecords(bulkEdit as any).then(() => {
                close();
                setFilter({...filter, bulkActions: false, checkAll: false, refreshTags: !filter.refreshTags});
                setPaginator(prev => ({...prev, page: 0}));
                refreshPagedBidders();
            }).catch(error => {
                formActions.setSubmitting(false);
                formActions.setStatus(getApiErrorMessage(error));
            });
        }
    });

    const replanLimits = modals.create<IReplanSpendLimitsModalProps>(ReplanSpendLimitsModal, {
        title: 'Re-plan spend limits of every enabled bidder',
        onSubmit: ({date}, formActions, close) => {
            return replanSpendLimits(date)
                .then(result => {
                    notify.success(`Re-planned spend limits of ${result.items.length} bidders for ${date}`);
                    close();
                })
                .catch(error => formActions.setStatus(getApiErrorMessage(error)));
        }
    });

    const applyFilter = (nextFilter: Partial<typeof filter>) => {
        setFilter({...filter, ...nextFilter, bulkActions: false, checkAll: false});
        setPaginator(prev => ({...prev, page: 0}));
    };

    const setBidderSearch = (value: string) => applyFilter({bidderSearch: value});
    const setAgencySearch = (value: string) => applyFilter({agencySearch: value});
    const setAuctionCategory = (value: 'performance' | 'branded' | null) => applyFilter({auctionCategory: value ?? null});
    const setTagFilter = (value: string | null) => applyFilter({tag: value ?? ''});

    const handleMutated = () => {
        setFilter({...filter, bulkActions: false, checkAll: false});
        refreshPagedBidders();
    };

    const handleEdit = (record: IBidderSummary) => {
        if (record?.id == null) {
            return;
        }
        fetchBidder(record.id)
            .then(bidder => editBidder.show({bidder}))
            .catch((error) => {
                notify.apiError(error, 'Failed to load bidder');
            });
    };

    const isPending = biddersQuery.isFetching;

    return <div className={style.bidders}>
        <div className="page-header-sticky">
            <div className="page-header-breadcrumbs">
                <Breadcrumbs/>
            </div>
            <Title className="page-header-title" toolbar={<>
                {ability.canCreate() && <Button intent={Intent.PRIMARY}
                    responsive
                    disabled={isPending}
                    leftIcon={<i className='fas fa-plus'/>}
                    onClick={() => navigate(biddersLinks.bidderCreate())}
                >Create</Button>}
                <Button intent={Intent.SUCCESS}
                    responsive
                    disabled={isPending}
                    leftIcon={<i className='fas fa-sync-alt'/>}
                    onClick={() => biddersQuery.refetch()}
                >Reload</Button>

                <Button key='edit' disabled={isPending || !filter.bulkActions} responsive intent={Intent.PRIMARY}
                    leftIcon={<i className='far fa-edit'/>}
                    onClick={() => biddersEdit.show()}>Edit</Button>

                <Button responsive disabled={isPending}
                    onClick={() => CSV.fromDataSource(
                        dataSource.state.records,
                        ['id',
                            'title',
                            'agencyId',
                            'agencyName',
                            'endpoint',
                            'auctionCategory',
                            'protocol',
                            'delayedNotification',
                            'minBidFloor',
                            'maxBidFloor',
                            'maxQps',
                            'tags',
                            'datacenters']
                    ).download('bidders.csv')}
                    leftIcon={<i className='far fa-file-excel'/>}
                >Export</Button>

                {ability.canEdit() && <Button responsive disabled={isPending}
                    leftIcon={<i className='fas fa-calendar-alt'/>}
                    onClick={() => replanLimits.show()}
                >Re-plan limits</Button>}
            </>}
            >Bidders</Title>
        </div>
        <BiddersTypeNavigation/>
        <div className="layout-content-box">
            {ability.canEdit() &&
            <form className={style.filter} onSubmit={(e) => {
                e.preventDefault();
            }}>
                <div className={style.searchRow}>
                    <Input
                        value={filter.bidderSearch}
                        onChange={setBidderSearch}
                        placeholder='Bidder contains'
                        className={style.bidderInput}
                        rightIcon={<i className='fa fa-search input-search-icon' />}
                    />
                    <Input
                        value={filter.agencySearch}
                        onChange={setAgencySearch}
                        placeholder='Agency contains'
                        className={style.agencyInput}
                        rightIcon={<i className='fa fa-search input-search-icon' />}
                    />
                    <SelectInput<'performance' | 'branded' | null>
                        items={[
                            {label: 'Performance', value: 'performance'},
                            {label: 'Branded', value: 'branded'}
                        ]}
                        value={filter.auctionCategory ?? null}
                        onChange={setAuctionCategory}
                        placeholder='Auction Category'
                        clearable
                        disabled={isPending}
                        className={style.categoryInput}
                    />
                    <SelectTags
                        refresh={filter.refreshTags}
                        placeholder='Filter by Tag'
                        value={filter.tag ?? ''}
                        clearable
                        onChange={setTagFilter}
                        className={style.tagInput}
                    />
                    <ToggleGroup
                        ariaLabel="Status"
                        items={[
                            {label: 'All', value: undefined},
                            {label: 'Active', value: true},
                            {label: 'Inactive', value: false}
                        ]}
                        value={filter.active}
                        disabled={isPending}
                        onChange={value => applyFilter({active: value})}
                    />
                </div>
            </form>
            }
            <BiddersTable
                dataSource={dataSource}
                onMutated={handleMutated}
                showCheck={true}
                filter={filter}
                setFilter={setFilter}
                isPending={isPending}
                serverFiltered
                disableClientSort
            />
            {pageResource.count > pageResource.pageSize &&
                <Pagination
                    padding={4}
                    offset={pageResource.pageSize * pageResource.page}
                    limit={pageResource.pageSize}
                    total={pageResource.count}
                    onChange={(page, pageSize) => {
                        setFilter({...filter, bulkActions: false, checkAll: false});
                        setPaginator({page, pageSize});
                    }}
                />
            }
        </div>
    </div>;
};
